import { useEffect, useState, useMemo, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import { useSelector, useDispatch } from "react-redux";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { isUndefined, debounce } from "lodash";
import { getAtlList } from "../../Actions/AtlAction";
import { globalStyle } from "../../global/Utils";
import MobileViewTabelList from "../Common/MobileViewTabelList";
import { formatCount, formatDateTime } from "./format";

const SEARCH_HINT = "Search by NTN, CNIC, name or business name";

const AtlList = ({ onGoToUpload }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");

  const {
    isGettingAtlList,
    atlList,
    totalResults,
    totalIsExact,
    mode,
    listMessage,
    listError,
    dataset,
    hasLoadedAtlStatus,
    hasLoadedAtlList,
  } = useSelector((state) => state.AtlReducer);

  // A finished import publishes a new list under a new id; asking again then
  // shows it without the admin having to reload the page.
  const liveImportId = dataset?.importId || "";

  useEffect(() => {
    dispatch(
      getAtlList(pagination.pageIndex, pagination.pageSize, globalFilter),
    );
  }, [pagination, dispatch, globalFilter, liveImportId]);

  // A new search always starts from its first page.
  const applyFilter = useCallback((value) => {
    setGlobalFilter(value);
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, []);

  const debouncedSetFilter = useMemo(
    () => debounce((value) => applyFilter(value), 500),
    [applyFilter],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "srNo",
        header: "#",
        size: 90,
        grow: false,
        enableResizing: false,
        Cell: ({ row }) =>
          formatCount(
            row.index + 1 + pagination.pageIndex * pagination.pageSize,
          ), // serial number considering pagination
      },
      {
        accessorKey: "ntn",
        header: "NTN / CNIC",
        size: 240,
        grow: false, // fixed width
      },
      {
        accessorKey: "name",
        header: "Name",
        grow: true,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "businessName",
        header: "Business Name",
        grow: true,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    ],
    [pagination],
  );

  const renderMobileCards = () => {
    if (!atlList.length) {
      return (
        <Box sx={{ p: 3, textAlign: "center", color: "#666", fontSize: 14 }}>
          No taxpayers found.
        </Box>
      );
    }
    return atlList.map((item, index) => (
      <Box
        key={`${item.srNo}-${item.ntn}-${index}`}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        {/* Serial Number */}
        <Box sx={{ fontSize: 12, color: "#999", mb: 1 }}>
          #{formatCount(index + 1 + pagination.pageIndex * pagination.pageSize)}
        </Box>

        {/* NTN / CNIC */}
        <Box sx={{ mb: 1 }}>
          <b className={`text-[${globalStyle.bgColorBlue}]`}>NTN / CNIC:</b>
          <Box sx={{ fontSize: 14 }}>{item.ntn || "-"}</Box>
        </Box>

        {/* Name */}
        <Box sx={{ mb: 1 }}>
          <b className={`text-[${globalStyle.bgColorBlue}]`}>Name:</b>
          <Box sx={{ fontSize: 14 }}>{item.name || "-"}</Box>
        </Box>

        {/* Business Name */}
        <Box>
          <b className={`text-[${globalStyle.bgColorBlue}]`}>Business Name:</b>
          <Box sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
            {item.businessName || "-"}
          </Box>
        </Box>
      </Box>
    ));
  };

  // Nothing has ever been imported: say so instead of showing an empty table.
  // An administrator learns it from the import status; anyone else from the
  // first search answer.
  if (
    (hasLoadedAtlStatus || hasLoadedAtlList) &&
    !dataset &&
    !isGettingAtlList
  ) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          fontFamily: globalStyle.fontFamilyParagraph,
        }}
      >
        <Box sx={{ fontSize: 18, fontWeight: 600, color: globalStyle.bgColorBlue }}>
          No taxpayer list has been uploaded yet
        </Box>
        {/* Only an administrator can upload; anyone else is given no way in. */}
        {onGoToUpload ? (
          <>
            <Box sx={{ mt: 1, mb: 2, fontSize: 14, color: "#666" }}>
              Upload the Active Taxpayer List file from FBR to search it here.
            </Box>
            <button
              type="button"
              onClick={onGoToUpload}
              className="px-4 py-2 rounded-md text-white font-semibold"
              style={{ background: globalStyle.buttonColor }}
            >
              Upload File
            </button>
          </>
        ) : (
          <Box sx={{ mt: 1, fontSize: 14, color: "#666" }}>
            Please ask an administrator to upload the Active Taxpayer List.
          </Box>
        )}
      </Box>
    );
  }

  const notice =
    listError ||
    listMessage ||
    (mode === "name" && !totalIsExact
      ? "Name searches show matches page by page; the total is not counted. Add another word to narrow the results."
      : "");

  return (
    <Box>
      {dataset && (
        <Box
          component="p"
          sx={{
            mb: 1.5,
            fontFamily: globalStyle.fontFamilyParagraph,
            fontSize: { xs: 12, sm: 14 },
            color: "#666",
          }}
        >
          <b>{formatCount(dataset.totalRows)}</b> active taxpayers
          {dataset.fileName ? ` · ${dataset.fileName}` : ""} · updated{" "}
          {formatDateTime(dataset.importedAt)}
        </Box>
      )}
      {notice && (
        <Box
          role={listError ? "alert" : "status"}
          sx={{
            mb: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1,
            fontSize: 14,
            fontFamily: globalStyle.fontFamilyParagraph,
            backgroundColor: listError ? "#fef2f2" : "#eff6ff",
            border: `1px solid ${listError ? "#fecaca" : "#bfdbfe"}`,
            color: listError ? "#b91c1c" : "#1e3a8a",
          }}
        >
          {notice}
        </Box>
      )}
      {isMobile ? (
        <MobileViewTabelList
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pagination={pagination}
          setPagination={setPagination}
          totalResults={totalResults}
          loading={isGettingAtlList}
          searchLabel="NTN, CNIC or Name"
        >
          {renderMobileCards()}
        </MobileViewTabelList>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={atlList}
          manualPagination
          manualFiltering
          rowCount={totalResults}
          enableSorting={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          state={{
            showGlobalFilter: true,
            isLoading: isGettingAtlList,
            pagination,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={(value) =>
            isUndefined(value)
              ? debouncedSetFilter("")
              : debouncedSetFilter(value)
          }
          muiSearchTextFieldProps={{
            placeholder: SEARCH_HINT,
            sx: { minWidth: 340 },
          }}
          // The last page of a name search is not known without counting
          // every match, so the jump-to-last button is only offered when it is.
          muiPaginationProps={{
            showLastButton: totalIsExact,
            rowsPerPageOptions: [25, 50, 100],
            sx: {
              "& .MuiTablePagination-toolbar": {
                fontFamily: globalStyle.fontFamily,
              },
              "& .Mui-selected": {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
              },
            },
          }}
          muiTablePaperProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              borderRadius: `${globalStyle.borderRadius}px`,
              fontFamily: globalStyle.fontFamily,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: globalStyle.blueColor,
              color: "#fff",
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 600,
              borderBottom: "none",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: globalStyle.fontSize,
              fontFamily: globalStyle.fontFamilyParagraph,
              color: "#333",
              borderBottom: `1px solid ${globalStyle.curveLightColor}`,
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: globalStyle.bgColorWhite,
              "&:hover": {
                backgroundColor: globalStyle.bgColor,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              maxHeight: "none",
              overflow: "visible",
              borderRadius: `${globalStyle.borderRadius}px`,
            },
          }}
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
            },
          }}
          renderEmptyRowsFallback={() => (
            <Box sx={{ p: 4, textAlign: "center", color: "#666" }}>
              No taxpayers found.
            </Box>
          )}
        />
      )}
    </Box>
  );
};

export default AtlList;
